import React, { useEffect, useState } from "react";
import {
  Button,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  SelectChangeEvent,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { showToast } from "@/app/global-components/toaster";
import useRegisterCompany from "@/hooks/use-register-company";
import { LANG } from "@/types";
import { CategoryOption, EditItem } from "@/pages/seller/articles";
import {
  Search,
  ChevronDown,
  X,
  Save,
  Package,
  Tag,
  Globe,
  FileText,
  Upload,
  AlertCircle,
} from "lucide-react";
import { countries, unitOptions } from "@/consts";

interface AddNewItemProps {
  isOpen: boolean;
  selectedItem: EditItem | null;
  Categoris: CategoryOption[];
  onClose: () => void;
  onSave: (newItem: any) => void;
  isLoading: boolean;
  error: string | null;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

type NewItem = {
  name: string;
  minOrder: number;
  price: number;
  descEn: string;
  descAr: string;
  unit: string;
  categories: string[] | string;
  type: string;
  brand: string;
  code: string;
  vat: string;
  image: File | null;
  imagePreview: string;
  countryOfOrigin: string;
  leadTime: string;
  attachments: File | null;
  pdfLink: string;
  sfdaMdma: string;
};

const AddNewItem: React.FC<AddNewItemProps> = ({
  Categoris,
  selectedItem,
  isOpen,
  onClose,
  onSave,
  isLoading,
  error,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { getCompanyMine } = useRegisterCompany();
  const [newItem, setNewItem] = useState<NewItem>({
    name: selectedItem?.name || "",
    minOrder: selectedItem?.minOrder || 0,
    descEn: selectedItem?.descEn || "",
    descAr: selectedItem?.descAr || "",
    categories: selectedItem?.categories || [],
    price: selectedItem?.price || 0,
    unit: selectedItem?.unit || "",
    type: selectedItem?.type || "Goods",
    brand: selectedItem?.brand || "",
    code: selectedItem?.code || "",
    vat: selectedItem?.vat || "",
    image: null as File | null,
    imagePreview: selectedItem?.imagePreview || "",
    countryOfOrigin: selectedItem?.countryOfOrigin || "",
    leadTime: selectedItem?.leadTime || "",
    attachments: null,
    pdfLink: selectedItem?.pdfLink || "",
    sfdaMdma: selectedItem?.sfdaMdma || "",
  });

  useEffect(() => {
    setNewItem({
      name: selectedItem?.name || "",
      minOrder: selectedItem?.minOrder || 0,
      descEn: selectedItem?.descEn || "",
      descAr: selectedItem?.descAr || "",
      price: selectedItem?.price || 0,
      unit: selectedItem?.unit || "",
      categories: selectedItem?.categories || [],
      type: selectedItem?.type || "Goods",
      brand: selectedItem?.brand || "",
      code: selectedItem?.code || "",
      vat: selectedItem?.vat || "",
      image: null,
      imagePreview: selectedItem?.imagePreview || "",
      countryOfOrigin: selectedItem?.countryOfOrigin || "",
      leadTime: selectedItem?.leadTime || "",
      attachments: null,
      pdfLink: selectedItem?.pdfLink || "",
      sfdaMdma: selectedItem?.sfdaMdma || "",
    });
  }, [selectedItem]);

  // Handler for MUI TextField (input/textarea)
  const handleTextFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "price") {
      setNewItem((prev) => ({
        ...prev,
        price: +value,
      }));
    } else {
      setNewItem((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handler for MUI Select
  const handleMuiSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewItem((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewItem((prev) => ({
        ...prev,
        attachments: file,
      }));
    }
  };

  // const [filterCategories, setFilterCategories] = useState<string[] | string>(
  //   []
  // );
  const [descLang, setDescLang] = useState<"en" | "ar">("en");
  const [countrySearch, setCountrySearch] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  // Filter countries based on search
  const filteredCountries = countries.filter((country) => {
    const searchTerm = countrySearch.toLowerCase();
    const countryName = locale === LANG.EN ? country.nameEn : country.nameAr;
    return countryName.toLowerCase().includes(searchTerm);
  });

  // Filter categories based on search
  const filteredCategories = Categoris.filter((category) => {
    const searchTerm = categorySearch.toLowerCase();
    return category.label.toLowerCase().includes(searchTerm);
  });

  // Handle category selection
  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const selectedValues = event.target.value;
    // setFilterCategories(selectedValues);
    setNewItem((prev) => ({
      ...prev,
      categories: selectedValues,
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".country-dropdown")) {
        setIsCountryDropdownOpen(false);
      }
    };

    if (isCountryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCountryDropdownOpen]);

  const handleSave = () => {
    if (!newItem.name.trim()) {
      showToast(t("Alert.NameRequired"), "warning");
      return;
    }

    const payload = {
      ...newItem,
      price: Number(newItem.price),
      vat: newItem.vat ? Number(newItem.vat) : null,
      categories: newItem.categories,
      ItemNR: myCompany.itemNr + 1,
      ...(selectedItem && { id: selectedItem.id }),
    };
    onSave(payload);
    getCompanyMine();
    setNewItem({
      name: "",
      price: 0,
      descEn: "",
      descAr: "",
      code: "",
      vat: "",
      categories: [],
      type: "Goods",
      unit: "",
      brand: "",
      image: null,
      imagePreview: "",
      countryOfOrigin: "",
      leadTime: "",
      attachments: null,
      pdfLink: "",
      minOrder: 0,
      sfdaMdma: "",
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      dir={locale === "ar" ? "rtl" : "ltr"}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
          m: 2,
          zIndex: 1400,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          borderBottom: "1px solid #e5e7eb",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 4,
                height: 32,
                background: "linear-gradient(135deg, #644980 0%, #1d4ed8 100%)",
                borderRadius: 2,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {selectedItem
                ? t("supplier_sidebar.edit_product")
                : t("item.add")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#f3f4f6",
                color: "#374151",
              },
            }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert
              severity="error"
              icon={<AlertCircle size={20} />}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Product Type Selection */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                  >
                    {t("item.fields.type")}*
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant={
                        newItem?.type === "Goods" ? "contained" : "outlined"
                      }
                      startIcon={<Package size={16} />}
                      onClick={() =>
                        setNewItem((prev) => ({ ...prev, type: "Goods" }))
                      }
                      sx={{
                        flex: 1,
                        backgroundColor:
                          newItem?.type === "Goods" ? "#18181b" : "transparent",
                        color: newItem?.type === "Goods" ? "white" : "#6b7280",
                        borderColor: "#d1d5db",
                      }}
                    >
                      Goods
                    </Button>
                    <Button
                      variant={
                        newItem?.type === "Services" ? "contained" : "outlined"
                      }
                      startIcon={<Globe size={16} />}
                      onClick={() =>
                        setNewItem((prev) => ({ ...prev, type: "Services" }))
                      }
                      sx={{
                        flex: 1,
                        backgroundColor:
                          newItem?.type === "Services"
                            ? "#18181b"
                            : "transparent",
                        color:
                          newItem?.type === "Services" ? "white" : "#6b7280",
                        borderColor: "#d1d5db",
                        "&:hover": {
                          backgroundColor:
                            newItem?.type === "Services" ? "" : "#f3f4f6",
                        },
                      }}
                    >
                      Services
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("item.fields.name")}
                name="name"
                value={newItem.name}
                onChange={handleTextFieldChange}
                required
                dir={locale === LANG.AR ? "rtl" : "ltr"}
                inputProps={{ dir: locale === LANG.AR ? "rtl" : "ltr" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  },
                  "& legend": {
                    textAlign: locale === LANG.AR ? "end" : "start",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("item.fields.code")}
                name="code"
                value={newItem.code || ""}
                onChange={handleTextFieldChange}
                placeholder={t("item.placeholders.entercode")}
                dir={locale === LANG.AR ? "rtl" : "ltr"}
                inputProps={{ dir: locale === LANG.AR ? "rtl" : "ltr" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  },
                  "& legend": {
                    textAlign: locale === LANG.AR ? "end" : "start",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("item.fields.brand")}
                name="brand"
                value={newItem?.brand || ""}
                onChange={handleTextFieldChange}
                placeholder={t("item.placeholders.enterBrand")}
                required
                dir={locale === LANG.AR ? "rtl" : "ltr"}
                inputProps={{ dir: locale === LANG.AR ? "rtl" : "ltr" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  },
                  "& legend": {
                    textAlign: locale === LANG.AR ? "end" : "start",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("item.fields.price")}
                name="price"
                type="number"
                inputProps={{ min: 0 }}
                value={newItem.price.toString()}
                onChange={handleTextFieldChange}
                required
                InputProps={{
                  dir: locale === LANG.AR ? "rtl" : "ltr",
                  startAdornment: (
                    <Tag
                      size={16}
                      style={{ marginRight: 8, color: "#6b7280" }}
                    />
                  ),
                }}
                dir={locale === LANG.AR ? "rtl" : "ltr"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  },
                  "& legend": {
                    textAlign: locale === LANG.AR ? "end" : "start",
                  },
                }}
              />
            </Grid>

            {/* Description Section */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      {t("item.fields.description")}*
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant={descLang === "en" ? "contained" : "outlined"}
                        onClick={() => setDescLang("en")}
                        sx={{
                          backgroundColor:
                            descLang === "en" ? "#18181b" : "transparent",
                          color: descLang === "en" ? "white" : "#6b7280",
                          borderColor: "#d1d5db",
                          minWidth: 40,
                          "&:hover": {
                            backgroundColor:
                              descLang === "en" ? "#18181b" : "#f3f4f6",
                          },
                        }}
                      >
                        EN
                      </Button>
                      <Button
                        size="small"
                        variant={descLang === "ar" ? "contained" : "outlined"}
                        onClick={() => setDescLang("ar")}
                        sx={{
                          backgroundColor:
                            descLang === "ar" ? "#18181b" : "transparent",
                          color: descLang === "ar" ? "white" : "#6b7280",
                          borderColor: "#d1d5db",
                          minWidth: 40,
                          "&:hover": {
                            backgroundColor:
                              descLang === "ar" ? "#18181b" : "#f3f4f6",
                          },
                        }}
                      >
                        AR
                      </Button>
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    name={descLang === "en" ? "descEn" : "descAr"}
                    value={descLang === "en" ? newItem.descEn : newItem.descAr}
                    onChange={handleTextFieldChange}
                    placeholder={
                      descLang === "en"
                        ? "Enter description in English..."
                        : "أدخل الوصف بالعربية..."
                    }
                    inputProps={{ dir: descLang === "en" ? "ltr" : "rtl" }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        direction: locale === LANG.AR ? "rtl" : "ltr",
                        "&:hover": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#644980",
                          },
                        },
                        "&.Mui-focused": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#644980",
                            borderWidth: 2,
                          },
                        },
                      },
                      "& .MuiInputLabel-root": {
                        transformOrigin: locale === LANG.AR ? "right" : "left",
                        direction: locale === LANG.AR ? "rtl" : "ltr",
                        ...(locale === LANG.AR
                          ? { right: 30, left: "auto" }
                          : { left: 0, right: "auto" }),
                      },
                      "& legend": {
                        textAlign: locale === LANG.AR ? "end" : "start",
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Additional Fields */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel
                  dir={locale === LANG.AR ? "rtl" : "ltr"}
                  sx={{
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  }}
                >
                  {t("item.fields.vat")}*
                </InputLabel>
                <Select
                  name="vat"
                  value={newItem.vat}
                  onChange={handleMuiSelectChange}
                  label={t("item.fields.vat") + "  d"}
                  dir={locale === LANG.AR ? "rtl" : "ltr"}
                  inputProps={{ dir: locale === LANG.AR ? "rtl" : "ltr" }}
                  sx={{
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "& .MuiSelect-select": {
                      paddingRight: "10px !important",
                    },
                    "& .MuiSelect-icon": {
                      left: locale === LANG.EN ? "unset" : 0,
                      marginLeft: 0,
                      right: locale === LANG.EN ? 0 : "unset",
                    },
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                    "& legend": {
                      textAlign: locale === LANG.AR ? "end" : "start",
                    },
                  }}
                >
                  <MenuItem value="0">0%</MenuItem>
                  <MenuItem value="15">15%</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel
                  dir={locale === LANG.AR ? "rtl" : "ltr"}
                  sx={{
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  }}
                >
                  {t("item.fields.categories")}*
                </InputLabel>
                <Select
                  multiple
                  open={isCategoryDropdownOpen}
                  onOpen={() => setIsCategoryDropdownOpen(true)}
                  onClose={() => {
                    setIsCategoryDropdownOpen(false);
                    setCategorySearch("");
                  }}
                  value={
                    Array.isArray(newItem.categories) ? newItem.categories : []
                  }
                  onChange={handleCategoryChange}
                  label={t("item.fields.categories") + "  d"}
                  inputProps={{ dir: locale === LANG.AR ? "rtl" : "ltr" }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                    onMouseDown: (e) => {
                      e.stopPropagation();
                    },
                    disableRestoreFocus: true,
                    keepMounted: true,
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value: string) => {
                        const category = Categoris.find(
                          (cat) => cat.value === value
                        );
                        return category ? (
                          <Chip
                            key={value}
                            label={category.label}
                            size="small"
                            sx={{
                              backgroundColor: "#e0e7ff",
                              color: "#3730a3",
                              fontSize: "0.75rem",
                            }}
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                  sx={{
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "& .MuiSelect-select": {
                      paddingRight: "10px !important",
                    },
                    "& .MuiSelect-icon": {
                      left: locale === LANG.EN ? "unset" : 0,
                      marginLeft: 0,
                      right: locale === LANG.EN ? 0 : "unset",
                    },
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                    "& legend": {
                      textAlign: locale === LANG.AR ? "end" : "start",
                    },
                  }}
                >
                  <Box sx={{ p: 1, borderBottom: "1px solid #e5e7eb" }}>
                    <TextField
                      size="small"
                      placeholder={
                        locale === LANG.EN
                          ? "Search categories..."
                          : "البحث في الفئات..."
                      }
                      value={categorySearch}
                      onChange={(e) => {
                        setCategorySearch(e.target.value);
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      InputProps={{
                        startAdornment: (
                          <Search
                            size={16}
                            style={{ marginRight: 8, color: "#6b7280" }}
                          />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>
                  {filteredCategories.map((name) => (
                    <MenuItem
                      key={name.value}
                      value={name.value}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      sx={{
                        borderRadius: 1,
                        margin: "2px 0",
                        "&.Mui-selected": {
                          backgroundColor: "#644980",
                          color: "#fff",
                          fontWeight: "600",
                          "&:hover": {
                            backgroundColor: "#644980",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#f3f4f6",
                        },
                      }}
                    >
                      {name.label}
                    </MenuItem>
                  ))}
                  {filteredCategories.length === 0 && (
                    <MenuItem
                      disabled
                      sx={{ textAlign: "center", color: "#6b7280" }}
                    >
                      {locale === LANG.EN
                        ? "No categories found"
                        : "لم يتم العثور على فئات"}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel
                  dir={locale === LANG.AR ? "rtl" : "ltr"}
                  sx={{
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  }}
                >
                  {t("item.fields.unit")}*
                </InputLabel>
                <Select
                  name="unit"
                  value={newItem?.unit || ""}
                  onChange={handleMuiSelectChange}
                  dir={locale === LANG.AR ? "rtl" : "ltr"}
                  label={t("item.fields.unit") + "   f"}
                  inputProps={{ dir: locale === LANG.AR ? "rtl" : "ltr" }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  }}
                  sx={{
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "& .MuiSelect-select": {
                      paddingRight: "10px !important",
                    },
                    "& .MuiSelect-icon": {
                      left: locale === LANG.EN ? "unset" : 0,
                      marginLeft: 0,
                      right: locale === LANG.EN ? 0 : "unset",
                    },
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                    "& legend": {
                      textAlign: locale === LANG.AR ? "end" : "start",
                    },
                  }}
                >
                  {unitOptions.map((unit) => (
                    <MenuItem
                      key={unit.value}
                      value={unit.value}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1,
                      }}
                    >
                      <span style={{ fontSize: "1.2em" }}>{unit.icon}</span>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {unit.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7280" }}>
                          {unit.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Image Upload */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                  >
                    {t("item.fields.image")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<Upload size={16} />}
                      sx={{
                        borderColor: "#d1d5db",
                        color: "#6b7280",
                        "&:hover": {
                          borderColor: "#644980",
                          color: "#644980",
                        },
                      }}
                    >
                      {t("actions.uploadImage")}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {newItem.imagePreview && (
                      <img
                        src={newItem.imagePreview}
                        alt="Preview"
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Country Selection */}
            <Grid
              sx={{ zIndex: 1000, position: "relative" }}
              item
              xs={12}
              md={6}
            >
              <Card
                sx={{
                  borderRadius: 2,
                  overflow: "visible",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                  >
                    {t("item.fields.countryOfOrigin")}
                  </Typography>
                  <div
                    className="country-dropdown"
                    style={{ position: "relative", zIndex: 1000 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setIsCountryDropdownOpen(!isCountryDropdownOpen)
                      }
                      endIcon={<ChevronDown size={16} />}
                      fullWidth
                      sx={{
                        justifyContent: "space-between",
                        borderColor: "#d1d5db",
                        color: "#6b7280",
                        "&:hover": {
                          borderColor: "#644980",
                          color: "#644980",
                        },
                      }}
                    >
                      {newItem.countryOfOrigin
                        ? countries.find(
                            (c) => c.code === newItem.countryOfOrigin
                          )?.[locale === LANG.EN ? "nameEn" : "nameAr"] ||
                          t("item.fields.selectCountry")
                        : t("item.fields.selectCountry")}
                    </Button>

                    {isCountryDropdownOpen && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          zIndex: 9999,
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          borderRadius: 2,
                          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                          mt: 0.5,
                          maxHeight: "250px",
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder={
                              locale === LANG.EN
                                ? "Search countries..."
                                : "البحث عن الدول..."
                            }
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <Search
                                  size={16}
                                  style={{ marginRight: 8, color: "#6b7280" }}
                                />
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1,
                              },
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            maxHeight: "calc(250px - 80px)",
                            overflowY: "auto",
                            "&::-webkit-scrollbar": {
                              width: "4px",
                            },
                            "&::-webkit-scrollbar-track": {
                              background: "#f1f1f1",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              background: "#c1c1c1",
                              borderRadius: "2px",
                            },
                          }}
                        >
                          {filteredCountries.map((country) => (
                            <Button
                              key={country.code}
                              fullWidth
                              onClick={() => {
                                setNewItem((prev) => ({
                                  ...prev,
                                  countryOfOrigin: country.code,
                                }));
                                setIsCountryDropdownOpen(false);
                                setCountrySearch("");
                              }}
                              sx={{
                                justifyContent: "flex-start",
                                px: 2,
                                py: 1,
                                textTransform: "none",
                                color: "#374151",
                                fontSize: "0.875rem",
                                "&:hover": {
                                  backgroundColor: "#f3f4f6",
                                },
                              }}
                            >
                              {
                                country[
                                  locale === LANG.EN ? "nameEn" : "nameAr"
                                ]
                              }
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("item.fields.leadTime")}
                name="leadTime"
                type="number"
                inputProps={{ min: 0, dir: locale === LANG.AR ? "rtl" : "ltr" }}
                value={newItem.leadTime || ""}
                onChange={handleTextFieldChange}
                placeholder={t("item.placeholders.numberOfDays")}
                dir={locale === LANG.AR ? "rtl" : "ltr"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  },
                  "& legend": {
                    textAlign: locale === LANG.AR ? "end" : "start",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("product_form.min_order")}
                name="minOrder"
                type="number"
                inputProps={{ min: 0, dir: locale === LANG.AR ? "rtl" : "ltr" }}
                value={newItem.minOrder || ""}
                onChange={handleTextFieldChange}
                dir={locale === LANG.AR ? "rtl" : "ltr"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  },
                  "& legend": {
                    textAlign: locale === LANG.AR ? "end" : "start",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="SFDA - MDMA"
                name="sfdaMdma"
                value={newItem.sfdaMdma || ""}
                onChange={handleTextFieldChange}
                placeholder="SFDA - MDMA"
                dir={locale === LANG.AR ? "rtl" : "ltr"}
                inputProps={{ dir: locale === LANG.AR ? "rtl" : "ltr" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  },
                  "& legend": {
                    textAlign: locale === LANG.AR ? "end" : "start",
                  },
                }}
              />
            </Grid>

            {/* Document Upload */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                  >
                    {t("item.fields.uploadDocument")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<FileText size={16} />}
                        fullWidth
                        sx={{
                          borderColor: "#d1d5db",
                          color: "#6b7280",
                          "&:hover": {
                            borderColor: "#644980",
                            color: "#644980",
                          },
                        }}
                      >
                        {t("actions.uploadPDF")}
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={handlePdfChange}
                        />
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("item.placeholders.orPastePdfLink")}
                        name="pdfLink"
                        value={newItem.pdfLink || ""}
                        onChange={handleTextFieldChange}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{ dir: locale === LANG.AR ? "rtl" : "ltr" }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          p: 3,
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          borderTop: "1px solid #e5e7eb",
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#d1d5db",
            color: "#6b7280",
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f3f4f6",
            },
          }}
        >
          {t("actions.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={
            isLoading ? <CircularProgress size={16} /> : <Save size={16} />
          }
          disabled={isLoading}
          sx={{
            background: "#18181b",
            color: "white",
            "&:disabled": {
              background: "#e5e7eb",
              color: "#9ca3af",
            },
          }}
        >
          {isLoading ? t("item.saving") : t("actions.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewItem;
